/**
 * activityApi — telemetry transport.
 *
 * Uses raw `fetch` rather than the shared axios instance, deliberately:
 *
 *  1. `keepalive: true` is required for the close beacon. A request started
 *     during page teardown is otherwise cancelled, which would lose exactly the
 *     data that makes session end-times accurate. axios uses XHR in the browser
 *     and cannot set it.
 *  2. `api_instance` retries 401s through the refresh flow. For fire-and-forget
 *     telemetry that is wrong — a stale token should drop the batch, not trigger
 *     a token rotation from a background beacon.
 *
 * Never throws and never rejects: telemetry must not surface to the user, nor
 * produce unhandled rejections in the console.
 */

import { tokenStorage } from "./tokenStorage";
import { API_ENDPOINTS } from "../constants/api";

const BASE_URL = process.env.REACT_APP_BASE_URL || "/api";

/**
 * Join a base URL and a path without producing a protocol-relative URL.
 *
 * `REACT_APP_BASE_URL` is `/` in .env.staging (same origin, proxied by the dev
 * server). Naive concatenation then yields `//activity/events`, which browsers
 * parse as PROTOCOL-RELATIVE — host becomes `activity` and the request goes to
 * `http://activity/events` and fails DNS. axios normalises this for us; raw
 * fetch does not.
 */
export const joinUrl = (base: string, path: string): string => {
  const trimmed = base.replace(/\/+$/, "");
  return trimmed ? `${trimmed}${path}` : path;
};

/**
 * POST a telemetry batch. The server replies 202 with no body.
 *
 * Resolves `true` when the batch reached the server and `false` when it did not,
 * so the caller can put the payload back rather than lose it. The caller must
 * drain its engaged-time counter BEFORE calling (the value goes in the body), and
 * previously any failure here discarded those milliseconds silently — both when
 * there was no token and when the fetch rejected.
 *
 * Still never throws and never rejects: telemetry must not surface to the user or
 * produce unhandled rejections in the console.
 */
export const postActivityBatch = async (body: unknown, keepalive: boolean): Promise<boolean> => {
  const token = tokenStorage.getAccessToken();
  // Unauthenticated: there is no session to attribute this to. Report NOT sent so
  // the engaged delta is credited back — a token refresh is usually in flight and
  // the next flush will carry it.
  if (!token) return false;

  try {
    const response = await fetch(joinUrl(BASE_URL, API_ENDPOINTS.ACTIVITY.EVENTS), {
      method: "POST",
      keepalive,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });
    // 4xx means the server rejected the payload itself — retrying cannot help, so
    // treat it as delivered and let it go rather than re-queueing forever. 5xx and
    // network failures are worth keeping.
    return response.ok || (response.status >= 400 && response.status < 500);
  } catch {
    return false;
  }
};
